import { useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import { useAppDispatch } from '@/utils/typeHelpers'
import { openPanel } from '@/features/adviser-chat/adviser-chat.slice'
import { isAdviserHiddenRoute } from '../lib/hiddenRoutes'
import { useLaunchHaven } from '@/features/adviser-chat/hooks/useLaunchHaven'
import { useOpenHavenFullScreen } from '@/features/adviser-chat/hooks/useOpenHavenFullScreen'
import {
  HAVEN_CONTEXT_QUERY_PARAM,
  HAVEN_ENTRY_QUERY_PARAM,
  HAVEN_VIEW_QUERY_PARAM,
  OPEN_HAVEN_QUERY_PARAM,
} from '@/features/adviser-chat/lib/havenRoutes'
import {
  HAVEN_ENTRY_POINT,
  HAVEN_LAUNCH_ACTIONS,
  type HavenEntryPoint,
  type HavenLaunchAction,
} from '../lib/types'

const isLaunchAction = (value: string | null): value is HavenLaunchAction =>
  HAVEN_LAUNCH_ACTIONS.some((action) => action === value)

const isEntryPoint = (value: string | null): value is HavenEntryPoint =>
  Object.values(HAVEN_ENTRY_POINT).some((entry) => entry === value)

export const useOpenHavenFromQueryParam = (): void => {
  const dispatch = useAppDispatch()
  const { pathname, key } = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const launchHaven = useLaunchHaven()
  const openFullScreen = useOpenHavenFullScreen()

  useEffect(() => {
    // A redirect that ran earlier in this commit (to /login, to the onboarding dashboard) already
    // moved the browser on; a replace built from this render's location would undo it.
    if (window.history.state?.key && window.history.state.key !== key) return
    if (searchParams.get(OPEN_HAVEN_QUERY_PARAM) !== 'true') return
    const contextValue = searchParams.get(HAVEN_CONTEXT_QUERY_PARAM)
    const entryValue = searchParams.get(HAVEN_ENTRY_QUERY_PARAM)
    const viewValue = searchParams.get(HAVEN_VIEW_QUERY_PARAM)
    const opensMessages =
      !isLaunchAction(contextValue) &&
      entryValue === HAVEN_ENTRY_POINT.sidebarMessages
    const next = new URLSearchParams(searchParams)
    next.delete(OPEN_HAVEN_QUERY_PARAM)
    next.delete(HAVEN_CONTEXT_QUERY_PARAM)
    next.delete(HAVEN_ENTRY_QUERY_PARAM)
    next.delete(HAVEN_VIEW_QUERY_PARAM)
    // Suppression patterns match pathname + search, so strip the haven params
    // before testing — otherwise exact-path patterns like /banking never match.
    const remainingSearch = next.toString()
    const route = remainingSearch ? `${pathname}?${remainingSearch}` : pathname
    // Strip first: a full-screen launch navigates away, and Back must not land on the launch link.
    setSearchParams(next, { replace: true })
    if (isAdviserHiddenRoute(route)) return
    if (isLaunchAction(contextValue)) {
      launchHaven({
        context: { type: 'action', action: contextValue },
        entryPoint: isEntryPoint(entryValue)
          ? entryValue
          : HAVEN_ENTRY_POINT.queryParam,
      })
    } else if (opensMessages) {
      const view = new URLSearchParams(
        viewValue ? { [HAVEN_VIEW_QUERY_PARAM]: viewValue } : {}
      ).toString()
      openFullScreen(null, view && `?${view}`)
    } else {
      dispatch(openPanel())
    }
  }, [
    searchParams,
    dispatch,
    setSearchParams,
    pathname,
    key,
    launchHaven,
    openFullScreen,
  ])
}
